/**
 * The creative list contract: the response `GET /api/creatives` answers from the company's
 * stored rows.
 *
 * The reference copy, with the query parameters, the error codes and the rules a server
 * has to keep, is `docs/reference/creative-list-contract.md`. This file is the client's
 * half: the types and the one shape check the screen runs before it trusts a body.
 *
 * The load-bearing decision: a creative is identified by `id`, the creative group id.
 * Nothing on the client keys anything by `displayName`, which is a label the buyer chose
 * and can repeat under two different assets.
 */

import type { Axis } from '../hypotheses/dictionaryApi'
import type { Lang } from '../i18n'
import { isPresetDays, type PresetDays } from '../lib/period'

/** Every measure the platform reports per ad, and the key `incomplete` lists. */
export type Measure =
  | 'spend'
  | 'impressions'
  | 'clicks'
  | 'conversions'
  | 'conversionValue'
  | 'threeSecondViews'
  | 'thruPlays'

export const MEASURES: readonly Measure[] = [
  'spend',
  'impressions',
  'clicks',
  'conversions',
  'conversionValue',
  'threeSecondViews',
  'thruPlays',
]

export interface ObservedMetrics {
  /** Each field is the sum over the in-scope ads that reported it, `null` when none did. */
  spend: number | null
  impressions: number | null
  clicks: number | null
  conversions: number | null
  conversionValue: number | null
  /** Video only: `null` for an image and when the platform returned no video metrics. */
  threeSecondViews: number | null
  thruPlays: number | null
  /** Measures some, but not all, in-scope ads reported. A `0` is a reported zero. */
  incomplete: Measure[]
}

export interface AdRef {
  platform: 'meta'
  adId: string
  accountId: string
  name: string
}

/**
 * The Format axis (GRO-174): what the creative is as a whole, which a carousel is not the
 * same question as what its representative asset is. `kind` stays two-valued, because the
 * video measures hang off it.
 */
export type CreativeFormat = 'image' | 'video' | 'carousel'

export interface CreativeSummary {
  /** The creative group id. The identity, opaque to the client. */
  id: string
  kind: 'video' | 'image'
  /** The served format; a server that predates GRO-174 omits it, which reads as `kind`. */
  format: CreativeFormat
  /**
   * The name of the highest-spend in-scope ad under this creative; a null spend sorts lowest
   * and ties break on the lower `adId`. A buyer's string: never translated, never identity.
   */
  displayName: string
  thumbnailUrl: string | null
  /** '1:1', '4:5', '9:16'. */
  aspectRatio: string | null
  durationSeconds: number | null
  /** Every platform ad merged under this creative, in scope. */
  ads: AdRef[]
  /** `null` renders as "no hypothesis assigned". */
  hypothesisId: string | null
  /**
   * The version letter inside the hypothesis, `A` to `Z`; `null` when unset or when no
   * hypothesis is assigned. An older server omits it, which reads as `null`.
   */
  versionLabel: string | null
  techniqueId: string | null
  /** `null` when unassigned. An older server omits it, which reads as `null`. */
  messageId: string | null
  /** `null` when unassigned. An older server omits it, which reads as `null`. */
  offerId: string | null
  /** The audiences the in-scope ads targeted; one entry when an audience is scoped. */
  audienceIds: string[]
  metrics: ObservedMetrics
  /** ROAS shrunk toward `benchmarks.roas` by the conversion count, computed per request. */
  adjustedRoas: number | null
  /**
   * Distinct days in the period with impressions above zero; `null` when no in-scope ad
   * reported impressions. An older server omits it, which reads as `null`.
   */
  activeDays: number | null
  /**
   * When Grovus first saw every in-scope ad of the creative paused (GRO-176), RFC 3339;
   * `null` when one of them runs or has no stored status. Display only. An older server
   * omits it, which reads as `null`.
   */
  pausedSince: string | null
  /**
   * The open tag suggestions of this creative (GRO-173), in the order the server serves
   * them. Empty for a guest. An older server omits it, which reads as `[]`.
   */
  suggestions: Suggestion[]
  /**
   * The server's verdict (GRO-182), which the client renders as served and never computes.
   * The contract requires it; an absent or `null` verdict decodes as `null` only as
   * defensive handling, and the client puts no verdict of its own in its place.
   */
  verdict: CreativeVerdict | null
}

export type VerdictKey = 'scale' | 'hold' | 'watch' | 'rework' | 'kill' | 'insufficient' | 'notStarted'

export type VerdictReason =
  
  | 'dataIncomplete'
  | 'noReference'
  | 'belowDirection'
  | 'belowProvisional'
  | 'noRoas'
  | 'noCpa'
  | 'better'
  | 'within'
  | 'worseDeliveryFine'
  | 'worseDeliveryWeak'
  | 'deliveryUnchecked'
  | 'neverDelivered'
  | 'stopLoss'

/** Why a scope has no reference; only `noConversions` claims no conversion history. */
export type ReferenceMissing =
  | 'noEligibleAds'
  | 'noSpend'
  | 'noConversions'
  | 'conversionsExcluded'
  | 'windowUncovered'
  | 'unplacedAds'
  | 'zeroRoas'
  | 'zeroCpa'

export interface StopLossDetail {
  /** The metric of the reference; absent from a server before GRO-185, read from the reference. */
  metric: VerdictMetric
  /** The creative's served spend, two decimals. */
  spend: number
  nPlus: number
  /** Null in CPA mode, which needs no AOV. */
  aov: number | null
  aovSource: 'creative' | 'reference' | null
  /** The best-case value of the metric: ROAS four decimals, CPA two decimals. */
  bestCase: number
  threshold: number
}

/** The company's primary metric (GRO-185); a verdict's reference is in it. */
export type VerdictMetric = 'roas' | 'cpa'

/**
 * The response-level basis of the verdicts (GRO-185): the company's primary metric, whether
 * it has a target, and whether the scope's currency is not the reporting currency. A body
 * from a server before GRO-185 has none, which reads as ROAS and no hint.
 */
export interface VerdictBasis {
  metric: VerdictMetric
  targetSet: boolean
  outsideReporting: boolean
  /**
   * The company's confidence tiers the verdicts were judged with (GRO-186). A body from a
   * server before GRO-186 has none, which reads as the defaults 20, 50 and 100.
   */
  tiers: VerdictTiers
}

export interface VerdictTiers {
  direction: number
  provisional: number
  reliable: number
}

export const DEFAULT_VERDICT_TIERS: VerdictTiers = { direction: 20, provisional: 50, reliable: 100 }

export interface CreativeVerdict {
  key: VerdictKey
  reason: VerdictReason
  /** The conversion count the tiers read; a null count is served as 0. */
  n: number
  /** Percent, signed, positive is better; null before the comparison. */
  gap: number | null
  /** Percent, two decimals; null when n = 0. */
  requiredGap: number | null
  /** Null on `noReference`. */
  reference: VerdictReference | null
  /** The delivery checks of a worse outcome; empty otherwise. */
  checks: DeliveryCheck[]
  /** Non-null on `stopLoss` only; null when an older backend omits it. */
  stopLoss: StopLossDetail | null
  /** Non-null on `noReference` only; null when an older backend omits it. */
  referenceMissing: ReferenceMissing | null
}

export interface VerdictReference {
  /** The company's target, or the 90-day average of the scope's currency (GRO-185). */
  kind: 'companyAverage90d' | 'target'
  metric: VerdictMetric
  /** ROAS four decimals, CPA two decimals in `currency`. */
  value: number
  currency: string
  from: string
  to: string
  /** The 90-day AOV; null when an older backend omits it. */
  aov: number | null
  /** The stop-loss threshold in `currency`; null when none can be computed or an older backend omits it. */
  stopLoss: number | null
  /** The scope's currency is not the reporting currency; absent from an older server, it reads as `false`. */
  outsideReporting: boolean
}

export interface DeliveryCheck {
  measure: 'ctr' | 'hookRate'
  value: number | null
  benchmark: number | null
  status: 'pass' | 'fail' | 'unavailable'
}

/** One open suggestion: `value` is data, shown as stored and never translated. */
export interface Suggestion {
  id: string
  field: Axis
  value: string
}

const SUGGESTION_FIELDS: readonly Axis[] = ['idea', 'visual', 'message', 'offer']

export interface AccountRef {
  id: string
  platform: 'meta'
  name: string
  short: string
  /** ISO 4217. */
  currency: string
  /** Ingestion's last successful pull for this account is older than the freshness interval. */
  lagging: boolean
  /** Seeded demo reporting: labelled, and never summed with a real account's figures. */
  isDemo: boolean
  /**
   * A demo account, or a real account a pull has completed for. `false` means its figures
   * are unknown, not zero. Absent from an older server, it reads as `true`.
   */
  reported: boolean
  /**
   * The current window only. `observedEmpty`: every day was read and nothing was delivered.
   * Absent from an older server, it reads as `false`, so no account is reported as empty.
   */
  window: { observedEmpty: boolean }
}

export interface Dimension {
  id: string
  label: Record<Lang, string>
}

/**
 * Account averages over the accounts in scope and the period, never narrowed by audience:
 * an audience is a cut inside the account, and the rules compare against the account.
 * `ctr`, `hookRate` and `holdRate` are percentages, 0 to 100, as the screen renders them.
 */
export interface Benchmarks {
  roas: number | null
  cpa: number | null
  ctr: number | null
  hookRate: number | null
  holdRate: number | null
}

/** Whether a window's figures can be compared. `checked` is the only state in which
 * `gaps` is filled: every in-scope creative not covered in the window, observed or not. */
export type WindowState = 'checked' | 'unverified' | 'inProgress'

/** A creative with the attributes the segment filters read, and nothing else. */
export interface CreativeRef {
  id: string
  kind: 'video' | 'image'
  /** Absent from a server that predates GRO-174, which reads as `kind`. */
  format: CreativeFormat
  hypothesisId: string | null
  techniqueId: string | null
  messageId: string | null
  offerId: string | null
}

export interface WindowCoverage {
  state: WindowState
  gaps: CreativeRef[]
}

/** The window the figures cover. `preset` is null for an explicit range. */
export interface PeriodEcho {
  days: number
  from: string
  to: string
  preset: PresetDays | null
  coverage: WindowCoverage
}

/** What the range picker may offer: nothing after `today`, nothing before `earliest`. */
export interface Bounds {
  today: string
  earliest: string | null
}

/** A creative observed in the previous window, with that window's figures. */
export interface PriorCreative extends CreativeRef {
  metrics: ObservedMetrics
}

export interface Comparison {
  /**
   * Same length as the current window, ending the day before it starts. `excludedAds` is
   * that window's count of ads an operator excluded, as `scope.excludedAds` is this one's.
   */
  period: { days: number; from: string; to: string; coverage: WindowCoverage; excludedAds: number }
  creatives: PriorCreative[]
}

/**
 * What the figures cover. `identifiedAds` when either window of the response holds an ad
 * an operator excluded, so the figures leave out ads that could not be identified.
 */
export type Basis = 'allAds' | 'identifiedAds'

/** One reason's unplaced ads; `pending` is an ad no pass has attempted yet. */
export interface UnplacedReason {
  reason: string
  ads: number
  excluded: boolean
}

/**
 * What was aggregated, echoed back, with how much of it the figures could not see. The
 * counts are scope-level and never narrowed by audience: no filter on the client removes
 * them, and any of them above zero makes every total partial.
 */
export interface ScopeEcho {
  accountIds: string[]
  audienceId: string | null
  /** Ads with delivery in the window that could not be placed under a creative, not excluded. */
  unplacedAds: number
  /** Unplaced ads with delivery in the window that an operator excluded. */
  excludedAds: number
  /** GRO-156 D3: the same ads by their last reason, excluded groups last. */
  unplacedReasons: UnplacedReason[]
  basis: Basis
  /** In-scope account-days in the window that no pull has observed yet. */
  uncoveredDays: number
}

/**
 * Which contract a body followed. The pre-GRO-65 server answers without `bounds` on the
 * list and without `period.preset` on the detail; its bodies are read by the old rules
 * and marked, so the shell can fall back rather than refuse them.
 */
export type ContractVersion = 'current' | 'old'

export interface CreativesResponse {
  contract: ContractVersion
  /** The company's dataset revision the response was read at. `null` from a server that
   * predates it, which the hypotheses round accepts without comparing. */
  revision: number | null
  /** The instant the response was evaluated at, RFC 3339 UTC. `null` as `revision` is. */
  asOf: string | null
  period: PeriodEcho
  /** `null` from the old contract. */
  bounds: Bounds | null
  /** `null` unless `compare=previous` was asked, and always from the old contract. */
  comparison: Comparison | null
  /** What was aggregated, echoed back, so the shell can widen or switch from here. */
  scope: ScopeEcho
  /** ISO 4217; every account in scope carries this one. `null` only when `accounts` is empty. */
  currency: string | null
  accounts: AccountRef[]
  dimensions: {
    hypotheses: Dimension[]
    techniques: Dimension[]
    messages: Dimension[]
    offers: Dimension[]
    audiences: Dimension[]
  }
  benchmarks: Benchmarks
  /** `null` from a server before GRO-185. */
  verdictBasis: VerdictBasis | null
  creatives: CreativeSummary[]
}

/**
 * The account ref of a split row: the account list's fields without `platform`, because
 * the split's reader already knows the platform from the ads it names.
 */
export interface SplitAccountRef {
  id: string
  name: string
  short: string
  /** ISO 4217. */
  currency: string
  /** Ingestion's last successful pull for this account is older than the freshness interval. */
  lagging: boolean
  /** Seeded demo reporting, as on the list's `AccountRef`. */
  isDemo: boolean
  /** As on the list's `AccountRef`. */
  reported: boolean
  /**
   * The business portfolio, what tells two same-named accounts apart (GRO-113). `null` for
   * a demo account, a portfolio Meta names without a name, and a server that predates it.
   */
  portfolioName: string | null
}

/** One account's cut of the detail: its in-scope ads under the creative, aggregated. */
export interface AccountSplit {
  account: SplitAccountRef
  ads: AdRef[]
  metrics: ObservedMetrics
}

/**
 * The detail contract: the response `GET /api/creatives/{id}` answers. One response
 * carries the merged figure, the per-account split and the account benchmarks, all
 * computed over the same in-scope ads, so the panel renders both from one body and the
 * split always adds up to the total beside it. The re-sum identity, `aggregateObserved(split.map((s) => s.metrics))`
 * deep-equal to `creative.metrics`, is asserted by tests on both sides of the wire.
 */
export interface CreativeDetailResponse {
  contract: ContractVersion
  period: PeriodEcho
  scope: ScopeEcho
  /** ISO 4217; the scope's one currency. */
  currency: string | null
  /** The merged figure, identical shape to the list's rows. */
  creative: CreativeSummary
  /** One per account the in-scope ads ran in, in the scope's own order. */
  split: AccountSplit[]
  /** The account benchmarks for this period and scope, the list's own shape. */
  benchmarks: Benchmarks
  /**
   * Why the merged row carries a mark no split does. With a note, the merged `incomplete`
   * is the splits' union plus the measures the note names; with none, the plain union.
   */
  notes: DetailNote[]
  /** `null` from a server before GRO-185. */
  verdictBasis: VerdictBasis | null
  /**
   * Every in-scope ad of the creative, each with the picture that ran under it (GRO-196).
   * `null` when the server sent no `ads`: a server before GRO-196, or a backend rollback.
   * The panel then hides the block; an empty list is a real answer and is not hidden.
   */
  ads: CreativeAd[] | null
}

/**
 * Why an ad is a member of its creative (PRD 0008 rule 9). `null` for an ad whose
 * membership predates the rule, which the panel shows as an empty cell.
 */
export type MembershipReason =
  | 'founder'
  | 'exact_bytes'
  | 'near_exact'
  | 'confirmed_suggestion'
  | 'merged_by_hand'
  | 'detached'

export const MEMBERSHIP_REASONS: readonly MembershipReason[] = [
  'founder', 'exact_bytes', 'near_exact', 'confirmed_suggestion', 'merged_by_hand', 'detached',
]

/** One row of the «Объявления» block. */
export interface CreativeAd {
  adId: string
  platform: 'meta'
  platformAdId: string
  accountId: string
  name: string
  status: string | null
  /** Spend in the scope's currency; `null` when no figure is observed for the ad. */
  spend: number | null
  spendIncomplete: boolean
  reason: MembershipReason | null
  thumbnailUrl: string | null
}

/**
 * `attributionAcrossAccounts`: each account's conversions sit under one attribution
 * setting, but not the same one, so the merged conversions and conversion value are marked.
 */
export type DetailNote = 'attributionAcrossAccounts'

export const DETAIL_NOTES: readonly DetailNote[] = ['attributionAcrossAccounts']

/** The `code` an error body carries. The screen renders the account refusals distinctly. */
export type CreativesErrorCode =
  | 'unknown_account'
  | 'empty_accounts'
  | 'mixed_provenance'
  | 'mixed_currency'
  | 'unknown_audience'
  | 'bad_period'

export const CREATIVES_ERROR_CODES: readonly CreativesErrorCode[] = [
  'unknown_account',
  'empty_accounts',
  'mixed_provenance',
  'mixed_currency',
  'unknown_audience',
  'bad_period',
]

/** The `code` a 404 detail body carries: no in-scope ad carries the requested creative. */
export const UNKNOWN_CREATIVE_ERROR_CODE = 'unknown_creative'

export function isCreativesErrorCode(value: unknown): value is CreativesErrorCode {
  return typeof value === 'string' && (CREATIVES_ERROR_CODES as readonly string[]).includes(value)
}

export interface CreativesErrorBody {
  error: { code: string; message: string; accounts?: string[] }
}

/** Reads `{ error: { code, accounts } }` out of a 400 body, or `null` if it is not one. */
export function readErrorBody(body: unknown): CreativesErrorBody['error'] | null {
  const error = (body as CreativesErrorBody | null)?.error
  if (!isRecord(error) || typeof error.code !== 'string') return null
  return {
    code: error.code,
    message: typeof error.message === 'string' ? error.message : '',
    accounts: Array.isArray(error.accounts) ? error.accounts.filter((a) => typeof a === 'string') : undefined,
  }
}

/**
 * The scope-level partial flag (D8): some delivery in scope is unseen, whether an ad that
 * could not be placed, a day not yet read, or ads an operator excluded. It holds for the
 * whole response, so the totals and the pools take it as an input and no filter over the
 * rows can remove it. Creative rows are not marked by it: the server marks them itself.
 */
export function scopePartial(scope: ScopeEcho): boolean {
  return scope.unplacedAds > 0 || scope.uncoveredDays > 0 || scope.basis === 'identifiedAds'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function fail(field: string, why: string): never {
  throw new Error(`creative response: ${field} ${why}`)
}

function requireRecord(value: unknown, field: string): Record<string, unknown> {
  if (!isRecord(value)) fail(field, 'is missing or not an object')
  return value
}

function requireArray(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) fail(field, 'is missing or not an array')
  return value
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string') fail(field, 'is missing or not a string')
  return value
}

function requireBoolean(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') fail(field, 'is missing or not a boolean')
  return value
}

function nullableNumber(value: unknown, field: string): number | null {
  if (value === null) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(field, 'is not a number or null')
  return value
}

function nullableString(value: unknown, field: string): string | null {
  if (value === null) return null
  if (typeof value !== 'string') fail(field, 'is not a string or null')
  return value
}

/** A field a server older than its axis omits: absent reads as `null`. */
function absentAsNull(value: unknown, field: string): string | null {
  return value === undefined ? null : nullableString(value, field)
}

/** The old contract's period: 14, 30 or 90 days, read as the preset it answered. */
function oldPeriod(value: Record<string, unknown>): PeriodEcho {
  const days = value.days
  if (days !== 14 && days !== 30 && days !== 90) fail('period.days', 'is not 14, 30 or 90')
  return {
    days,
    from: requireString(value.from, 'period.from'),
    to: requireString(value.to, 'period.to'),
    preset: days,
    coverage: { state: 'unverified', gaps: [] },
  }
}

function positiveInteger(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) fail(field, 'is not a positive integer')
  return value
}

function parseKind(value: unknown, field: string): 'video' | 'image' {
  if (value !== 'video' && value !== 'image') fail(field, 'is not video or image')
  return value
}

/**
 * The format, with the pre-GRO-174 fallback the design's D4 asks for: a response without
 * `format` decodes with `format` equal to `kind`, so the frontend deploys in either order
 * against the API and a wrong value still fails loudly.
 */
function parseFormat(value: unknown, kind: 'video' | 'image', field: string): CreativeFormat {
  if (value === undefined) return kind
  if (value !== 'carousel' && value !== 'video' && value !== 'image') {
    fail(field, 'is not carousel, video or image')
  }
  return value
}

function parseCreativeRef(value: unknown, field: string): CreativeRef {
  const c = requireRecord(value, field)
  const kind = parseKind(c.kind, `${field}.kind`)
  return {
    id: requireString(c.id, `${field}.id`),
    kind,
    format: parseFormat(c.format, kind, `${field}.format`),
    hypothesisId: nullableString(c.hypothesisId, `${field}.hypothesisId`),
    techniqueId: nullableString(c.techniqueId, `${field}.techniqueId`),
    messageId: absentAsNull(c.messageId, `${field}.messageId`),
    offerId: absentAsNull(c.offerId, `${field}.offerId`),
  }
}

function parseCoverage(value: unknown, field: string): WindowCoverage {
  const c = requireRecord(value, field)
  const state = c.state
  if (state !== 'checked' && state !== 'unverified' && state !== 'inProgress') {
    fail(`${field}.state`, 'is not checked, unverified or inProgress')
  }
  return {
    state,
    gaps: requireArray(c.gaps, `${field}.gaps`).map((entry, i) => parseCreativeRef(entry, `${field}.gaps[${i}]`)),
  }
}

function parsePeriodEcho(value: Record<string, unknown>): PeriodEcho {
  const preset = value.preset
  if (preset !== null && !isPresetDays(preset)) fail('period.preset', 'is not 7, 14, 30, 60, 90 or null')
  return {
    days: positiveInteger(value.days, 'period.days'),
    from: requireString(value.from, 'period.from'),
    to: requireString(value.to, 'period.to'),
    preset,
    coverage: parseCoverage(value.coverage, 'period.coverage'),
  }
}

function count(value: unknown, field: string): number {
  if (value === undefined) return 0
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) fail(field, 'is not a count')
  return value
}

/** The scope echo. A server before GRO-44 sends neither the counts nor the basis; they
 * read as `0` and `allAds`, which is what that server's figures meant. A server before
 * GRO-156 sends no `unplacedReasons`; it reads as no reason lines. */
function parseScope(value: unknown): ScopeEcho {
  const scope = requireRecord(value, 'scope')
  const basis = scope.basis ?? 'allAds'
  if (basis !== 'allAds' && basis !== 'identifiedAds') fail('scope.basis', 'is not allAds or identifiedAds')
  return {
    accountIds: requireArray(scope.accountIds, 'scope.accountIds').map((id, i) =>
      requireString(id, `scope.accountIds[${i}]`),
    ),
    audienceId: nullableString(scope.audienceId, 'scope.audienceId'),
    unplacedAds: count(scope.unplacedAds, 'scope.unplacedAds'),
    excludedAds: count(scope.excludedAds, 'scope.excludedAds'),
    unplacedReasons: parseReasons(scope.unplacedReasons),
    basis,
    uncoveredDays: count(scope.uncoveredDays, 'scope.uncoveredDays'),
  }
}

function parseReasons(value: unknown): UnplacedReason[] {
  if (value === undefined) return []
  return requireArray(value, 'scope.unplacedReasons').map((item, i) => {
    const field = `scope.unplacedReasons[${i}]`
    const group = requireRecord(item, field)
    if (typeof group.excluded !== 'boolean') fail(`${field}.excluded`, 'is not a boolean')
    return {
      reason: requireString(group.reason, `${field}.reason`),
      ads: count(group.ads, `${field}.ads`),
      excluded: group.excluded,
    }
  })
}

function parseNotes(value: unknown): DetailNote[] {
  if (value === undefined) return []
  return requireArray(value, 'notes').map((entry, i) => {
    if (!(DETAIL_NOTES as readonly unknown[]).includes(entry)) fail(`notes[${i}]`, 'is not a detail note')
    return entry as DetailNote
  })
}

function parseSuggestions(value: unknown, field: string): Suggestion[] {
  if (value === undefined) return []
  return requireArray(value, field).map((entry, i) => {
    const s = requireRecord(entry, `${field}[${i}]`)
    if (!(SUGGESTION_FIELDS as readonly unknown[]).includes(s.field)) fail(`${field}[${i}].field`, 'is not an axis')
    return {
      id: requireString(s.id, `${field}[${i}].id`),
      field: s.field as Axis,
      value: requireString(s.value, `${field}[${i}].value`),
    }
  })
}

function parseBounds(value: unknown): Bounds {
  const b = requireRecord(value, 'bounds')
  return {
    today: requireString(b.today, 'bounds.today'),
    earliest: nullableString(b.earliest, 'bounds.earliest'),
  }
}

function parseComparison(value: unknown): Comparison | null {
  if (value === null) return null
  const c = requireRecord(value, 'comparison')
  const period = requireRecord(c.period, 'comparison.period')
  return {
    period: {
      days: positiveInteger(period.days, 'comparison.period.days'),
      from: requireString(period.from, 'comparison.period.from'),
      to: requireString(period.to, 'comparison.period.to'),
      coverage: parseCoverage(period.coverage, 'comparison.period.coverage'),
      excludedAds: count(period.excludedAds, 'comparison.period.excludedAds'),
    },
    creatives: requireArray(c.creatives, 'comparison.creatives').map((entry, i) => ({
      ...parseCreativeRef(entry, `comparison.creatives[${i}]`),
      metrics: parseMetrics(requireRecord(entry, `comparison.creatives[${i}]`).metrics, `comparison.creatives[${i}].metrics`),
    })),
  }
}

function measures(value: unknown, field: string): Measure[] {
  return requireArray(value, field).map((entry, i) => {
    if (!(MEASURES as readonly unknown[]).includes(entry)) fail(`${field}[${i}]`, 'is not a measure name')
    return entry as Measure
  })
}

function parseMetrics(value: unknown, field: string): ObservedMetrics {
  const m = requireRecord(value, field)
  return {
    spend: nullableNumber(m.spend, `${field}.spend`),
    impressions: nullableNumber(m.impressions, `${field}.impressions`),
    clicks: nullableNumber(m.clicks, `${field}.clicks`),
    conversions: nullableNumber(m.conversions, `${field}.conversions`),
    conversionValue: nullableNumber(m.conversionValue, `${field}.conversionValue`),
    threeSecondViews: nullableNumber(m.threeSecondViews, `${field}.threeSecondViews`),
    thruPlays: nullableNumber(m.thruPlays, `${field}.thruPlays`),
    incomplete: measures(m.incomplete, `${field}.incomplete`),
  }
}

function parseDimensions(value: unknown, field: string): Dimension[] {
  return requireArray(value, field).map((entry, i) => {
    const d = requireRecord(entry, `${field}[${i}]`)
    const label = requireRecord(d.label, `${field}[${i}].label`)
    const en = requireString(label.en, `${field}[${i}].label.en`)
    return {
      id: requireString(d.id, `${field}[${i}].id`),
      label: {
        en,
        ru: requireString(label.ru, `${field}[${i}].label.ru`),
        // A server older than the third language sends no `uk`; the label reads in English.
        uk: label.uk === undefined ? en : requireString(label.uk, `${field}[${i}].label.uk`),
      },
    }
  })
}

function parseBenchmarks(value: unknown, field: string): Benchmarks {
  const b = requireRecord(value, field)
  return {
    roas: nullableNumber(b.roas, `${field}.roas`),
    cpa: nullableNumber(b.cpa, `${field}.cpa`),
    ctr: nullableNumber(b.ctr, `${field}.ctr`),
    hookRate: nullableNumber(b.hookRate, `${field}.hookRate`),
    holdRate: nullableNumber(b.holdRate, `${field}.holdRate`),
  }
}

function parseAdRef(value: unknown, field: string): AdRef {
  const ad = requireRecord(value, field)
  return {
    platform: 'meta' as const,
    adId: requireString(ad.adId, `${field}.adId`),
    accountId: requireString(ad.accountId, `${field}.accountId`),
    name: requireString(ad.name, `${field}.name`),
  }
}

export const VERDICT_KEYS: readonly VerdictKey[] = [
  'scale', 'hold', 'watch', 'rework', 'kill', 'insufficient', 'notStarted',
]
export const VERDICT_REASONS: readonly VerdictReason[] = [
  'dataIncomplete', 'noReference', 'belowDirection', 'belowProvisional', 'noRoas', 'noCpa',
  'better', 'within', 'worseDeliveryFine', 'worseDeliveryWeak', 'deliveryUnchecked', 'neverDelivered', 'stopLoss',
]
export const REFERENCE_MISSING: readonly ReferenceMissing[] = [
  'noEligibleAds', 'noSpend', 'noConversions', 'conversionsExcluded', 'windowUncovered',
  'unplacedAds', 'zeroRoas', 'zeroCpa',
]
const METRICS: readonly VerdictMetric[] = ['roas', 'cpa']

function oneOf<T extends string>(value: unknown, allowed: readonly T[], field: string): T {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    fail(field, `is not one of ${allowed.join(', ')}`)
  }
  return value as T
}

function requireNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(field, 'is missing or not a number')
  return value
}

function parseVerdictReference(value: unknown, field: string): VerdictReference | null {
  if (value === null) return null
  const r = requireRecord(value, field)
  return {
    kind: oneOf(r.kind, ['companyAverage90d', 'target'] as const, `${field}.kind`),
    metric: oneOf(r.metric, METRICS, `${field}.metric`),
    value: requireNumber(r.value, `${field}.value`),
    currency: requireString(r.currency, `${field}.currency`),
    from: requireString(r.from, `${field}.from`),
    to: requireString(r.to, `${field}.to`),
    aov: r.aov === undefined ? null : nullableNumber(r.aov, `${field}.aov`),
    stopLoss: r.stopLoss === undefined ? null : nullableNumber(r.stopLoss, `${field}.stopLoss`),
    outsideReporting: r.outsideReporting === undefined ? false : requireBoolean(r.outsideReporting, `${field}.outsideReporting`),
  }
}

/** `fallback` is the reference's metric, which a stop-loss detail before GRO-185 omits. */
function parseStopLoss(value: unknown, field: string, fallback: VerdictMetric): StopLossDetail | null {
  if (value === undefined || value === null) return null
  const d = requireRecord(value, field)
  return {
    metric: d.metric === undefined ? fallback : oneOf(d.metric, METRICS, `${field}.metric`),
    spend: requireNumber(d.spend, `${field}.spend`),
    nPlus: requireNumber(d.nPlus, `${field}.nPlus`),
    aov: d.aov === undefined ? null : nullableNumber(d.aov, `${field}.aov`),
    aovSource:
      d.aovSource === undefined || d.aovSource === null
        ? null
        : oneOf(d.aovSource, ['creative', 'reference'] as const, `${field}.aovSource`),
    bestCase: requireNumber(d.bestCase, `${field}.bestCase`),
    threshold: requireNumber(d.threshold, `${field}.threshold`),
  }
}

function parseVerdictBasis(value: unknown): VerdictBasis | null {
  if (value === undefined || value === null) return null
  const b = requireRecord(value, 'verdictBasis')
  return {
    metric: oneOf(b.metric, METRICS, 'verdictBasis.metric'),
    targetSet: requireBoolean(b.targetSet, 'verdictBasis.targetSet'),
    outsideReporting: requireBoolean(b.outsideReporting, 'verdictBasis.outsideReporting'),
    tiers: parseVerdictTiers(b.tiers),
  }
}

function parseVerdictTiers(value: unknown): VerdictTiers {
  if (value === undefined || value === null) return DEFAULT_VERDICT_TIERS
  const t = requireRecord(value, 'verdictBasis.tiers')
  return {
    direction: positiveInteger(t.direction, 'verdictBasis.tiers.direction'),
    provisional: positiveInteger(t.provisional, 'verdictBasis.tiers.provisional'),
    reliable: positiveInteger(t.reliable, 'verdictBasis.tiers.reliable'),
  }
}

function parseDeliveryCheck(value: unknown, field: string): DeliveryCheck {
  const c = requireRecord(value, field)
  return {
    measure: oneOf(c.measure, ['ctr', 'hookRate'] as const, `${field}.measure`),
    value: nullableNumber(c.value, `${field}.value`),
    benchmark: nullableNumber(c.benchmark, `${field}.benchmark`),
    status: oneOf(c.status, ['pass', 'fail', 'unavailable'] as const, `${field}.status`),
  }
}

/**
 * The served verdict, field by field. An absent or `null` verdict is `null`: defensive
 * handling of a body the contract does not allow, with no verdict computed in its place.
 */
function parseVerdict(value: unknown, field: string): CreativeVerdict | null {
  if (value === undefined || value === null) return null
  const v = requireRecord(value, field)
  const reference = parseVerdictReference(v.reference, `${field}.reference`)
  return {
    key: oneOf(v.key, VERDICT_KEYS, `${field}.key`),
    reason: oneOf(v.reason, VERDICT_REASONS, `${field}.reason`),
    n: requireNumber(v.n, `${field}.n`),
    gap: nullableNumber(v.gap, `${field}.gap`),
    requiredGap: nullableNumber(v.requiredGap, `${field}.requiredGap`),
    reference,
    checks: requireArray(v.checks, `${field}.checks`).map((entry, j) =>
      parseDeliveryCheck(entry, `${field}.checks[${j}]`),
    ),
    stopLoss: parseStopLoss(v.stopLoss, `${field}.stopLoss`, reference?.metric ?? 'roas'),
    referenceMissing:
      v.referenceMissing === undefined || v.referenceMissing === null
        ? null
        : oneOf(v.referenceMissing, REFERENCE_MISSING, `${field}.referenceMissing`),
  }
}

/** The creative shape both the list's rows and the detail's `creative` field carry. */
function parseCreativeSummary(value: unknown, field: string): CreativeSummary {
  const c = requireRecord(value, field)
  const kind = parseKind(c.kind, `${field}.kind`)
  return {
    id: requireString(c.id, `${field}.id`),
    kind,
    format: parseFormat(c.format, kind, `${field}.format`),
    displayName: requireString(c.displayName, `${field}.displayName`),
    thumbnailUrl: nullableString(c.thumbnailUrl, `${field}.thumbnailUrl`),
    aspectRatio: nullableString(c.aspectRatio, `${field}.aspectRatio`),
    durationSeconds: nullableNumber(c.durationSeconds, `${field}.durationSeconds`),
    ads: requireArray(c.ads, `${field}.ads`).map((adEntry, j) =>
      parseAdRef(adEntry, `${field}.ads[${j}]`),
    ),
    hypothesisId: nullableString(c.hypothesisId, `${field}.hypothesisId`),
    versionLabel:
      c.versionLabel === undefined ? null : nullableString(c.versionLabel, `${field}.versionLabel`),
    techniqueId: nullableString(c.techniqueId, `${field}.techniqueId`),
    messageId: absentAsNull(c.messageId, `${field}.messageId`),
    offerId: absentAsNull(c.offerId, `${field}.offerId`),
    audienceIds: requireArray(c.audienceIds, `${field}.audienceIds`).map((id, j) =>
      requireString(id, `${field}.audienceIds[${j}]`),
    ),
    metrics: parseMetrics(c.metrics, `${field}.metrics`),
    adjustedRoas: nullableNumber(c.adjustedRoas, `${field}.adjustedRoas`),
    activeDays:
      c.activeDays === undefined ? null : nullableNumber(c.activeDays, `${field}.activeDays`),
    pausedSince: absentAsNull(c.pausedSince, `${field}.pausedSince`),
    suggestions: parseSuggestions(c.suggestions, `${field}.suggestions`),
    verdict: parseVerdict(c.verdict, `${field}.verdict`),
  }
}

/**
 * The one shape check on the client. It rejects a body missing any envelope field, naming
 * the field, so a server that drops `dimensions` fails here and not three components later
 * with an undefined read. It does not validate figures: a number the server computed wrong
 * is a server defect, and pretending to catch it here would only hide where it came from.
 *
 * Which contract the body follows is decided first, by `bounds`: the pre-GRO-65 server
 * never sends it. An old body is read by the old rules and marked `old`, not refused.
 */
export function parseCreativesResponse(body: unknown): CreativesResponse {
  const root = requireRecord(body, 'body')
  const contract: ContractVersion = 'bounds' in root ? 'current' : 'old'

  const period = requireRecord(root.period, 'period')
  const accounts = requireArray(root.accounts, 'accounts')
  const dimensions = requireRecord(root.dimensions, 'dimensions')
  const currency = nullableString(root.currency, 'currency')
  if (currency === null && accounts.length > 0) {
    fail('currency', 'is null while accounts are in scope')
  }

  return {
    contract,
    revision: nullableNumber(root.revision ?? null, 'revision'),
    asOf: nullableString(root.as_of ?? null, 'as_of'),
    period: contract === 'current' ? parsePeriodEcho(period) : oldPeriod(period),
    bounds: contract === 'current' ? parseBounds(root.bounds) : null,
    comparison: contract === 'current' ? parseComparison(root.comparison ?? null) : null,
    scope: parseScope(root.scope),
    currency,
    accounts: accounts.map((entry, i) => {
      const a = requireRecord(entry, `accounts[${i}]`)
      return {
        id: requireString(a.id, `accounts[${i}].id`),
        platform: 'meta' as const,
        name: requireString(a.name, `accounts[${i}].name`),
        short: requireString(a.short, `accounts[${i}].short`),
        currency: requireString(a.currency, `accounts[${i}].currency`),
        lagging: a.lagging === true,
        isDemo: requireBoolean(a.isDemo, `accounts[${i}].isDemo`),
        reported: a.reported !== false,
        window: { observedEmpty: isRecord(a.window) && a.window.observedEmpty === true },
      }
    }),
    dimensions: {
      hypotheses: parseDimensions(dimensions.hypotheses, 'dimensions.hypotheses'),
      techniques: parseDimensions(dimensions.techniques, 'dimensions.techniques'),
      // A server older than the message and offer axes sends neither list.
      messages: parseDimensions(dimensions.messages ?? [], 'dimensions.messages'),
      offers: parseDimensions(dimensions.offers ?? [], 'dimensions.offers'),
      audiences: parseDimensions(dimensions.audiences, 'dimensions.audiences'),
    },
    benchmarks: parseBenchmarks(root.benchmarks, 'benchmarks'),
    verdictBasis: parseVerdictBasis(root.verdictBasis),
    creatives: requireArray(root.creatives, 'creatives').map((entry, i) =>
      parseCreativeSummary(entry, `creatives[${i}]`),
    ),
  }
}

/**
 * The one shape check on the detail, mirroring the list's: it rejects a body missing any
 * envelope field, naming the field, and does not validate figures - a wrong number is a
 * server defect, and pretending to catch it here would only hide where it came from.
 *
 * The detail carries no `bounds` in either contract, so its discriminator is
 * `period.preset`, which the pre-GRO-65 server never sends.
 */
export function parseCreativeDetailResponse(body: unknown): CreativeDetailResponse {
  const root = requireRecord(body, 'body')

  const period = requireRecord(root.period, 'period')
  const contract: ContractVersion = 'preset' in period ? 'current' : 'old'

  return {
    contract,
    period: contract === 'current' ? parsePeriodEcho(period) : oldPeriod(period),
    scope: parseScope(root.scope),
    currency: nullableString(root.currency, 'currency'),
    creative: parseCreativeSummary(root.creative, 'creative'),
    split: requireArray(root.split, 'split').map((entry, i) => {
      const s = requireRecord(entry, `split[${i}]`)
      const account = requireRecord(s.account, `split[${i}].account`)
      return {
        account: {
          id: requireString(account.id, `split[${i}].account.id`),
          name: requireString(account.name, `split[${i}].account.name`),
          short: requireString(account.short, `split[${i}].account.short`),
          currency: requireString(account.currency, `split[${i}].account.currency`),
          lagging: account.lagging === true,
          isDemo: requireBoolean(account.isDemo, `split[${i}].account.isDemo`),
          reported: account.reported !== false,
          portfolioName:
            typeof account.portfolioName === 'string' && account.portfolioName !== ''
              ? account.portfolioName
              : null,
        },
        ads: requireArray(s.ads, `split[${i}].ads`).map((adEntry, j) =>
          parseAdRef(adEntry, `split[${i}].ads[${j}]`),
        ),
        metrics: parseMetrics(s.metrics, `split[${i}].metrics`),
      }
    }),
    benchmarks: parseBenchmarks(root.benchmarks, 'benchmarks'),
    notes: parseNotes(root.notes),
    verdictBasis: parseVerdictBasis(root.verdictBasis),
    ads: parseCreativeAds(root.ads),
  }
}

/** Absent `ads` reads as "not available" (`null`), distinct from an empty list. */
function parseCreativeAds(value: unknown): CreativeAd[] | null {
  if (value === undefined || value === null) return null
  return requireArray(value, 'ads').map((entry, i) => {
    const field = `ads[${i}]`
    const ad = requireRecord(entry, field)
    return {
      adId: requireString(ad.adId, `${field}.adId`),
      platform: 'meta' as const,
      platformAdId: requireString(ad.platformAdId, `${field}.platformAdId`),
      accountId: requireString(ad.accountId, `${field}.accountId`),
      name: requireString(ad.name, `${field}.name`),
      status: absentAsNull(ad.status, `${field}.status`),
      spend: ad.spend === undefined ? null : nullableNumber(ad.spend, `${field}.spend`),
      spendIncomplete: ad.spendIncomplete === true,
      reason:
        ad.reason === undefined || ad.reason === null
          ? null
          : oneOf(ad.reason, MEMBERSHIP_REASONS, `${field}.reason`),
      thumbnailUrl: absentAsNull(ad.thumbnailUrl, `${field}.thumbnailUrl`),
    }
  })
}
